import React, { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useSelector } from "react-redux";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { selectFoldersForFolderMove, useMoveFolderMutation } from "fond/api";
import { HOME_FOLDER_ID } from "fond/constants";
import { FolderPickerField } from "fond/form/fields/FolderPickerField";
import { Folder, Store } from "fond/types";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { Message, Modal } from "fond/widgets";

interface IFormData {
  FolderId: string;
}

interface IProps {
  /**
   * The Folder being moved
   */
  folder: Folder;
  /**
   * Callback function to handle the onClose of the modal
   */
  onClose(): void;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onConfirm(): Promise<void>;
  onClose(): void;
  folderName: string;
  isSaving: boolean;
  type: "project" | "folder";
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ isOpen, onClose, onConfirm, folderName, isSaving, type }: ConfirmationModalProps) => (
  <Modal
    open={isOpen}
    header={`Move ${type}?`}
    data-testid="confirm-move-folder-modal"
    content={
      <Box>
        <Typography>
          Are you sure you wish to move the {type} <strong>{folderName}</strong>?
        </Typography>
        <Typography>
          Moving a {type} to a new location has the potential to change who can access & edit this {type} and its contents.
        </Typography>
      </Box>
    }
    actions={
      <>
        <Button data-testid="move-folder-confirm-cancel-button" color="primary" onClick={onClose} sx={{ marginRight: 1 }}>
          Cancel
        </Button>
        <LoadingButton data-testid="move-folder-confirm-button" color="primary" onClick={onConfirm} loading={isSaving}>
          Confirm
        </LoadingButton>
      </>
    }
  />
);

const MoveFolderDialog: React.FC<IProps> = ({ folder, onClose }: IProps) => {
  const [confirmModal, showConfirmModal] = useState<boolean>(false);
  const [selectedFolder, setSelectedFolder] = useState<Pick<Folder, "ID" | "ParentID">>();
  const { enqueueSnackbar } = useSnackbar();
  const [moveFolder, { isLoading: isSaving, isError }] = useMoveFolderMutation();
  const folders = useSelector((state: Store) => selectFoldersForFolderMove(state, folder));
  const canMove = permissionCheck(folder.Permission.Level, Actions.FOLDER_MOVE);
  const type = folder.MultiProject ? "project" : "folder";
  const foldersExceptSelf = useMemo(() => folders.filter((currFolder: Folder) => currFolder.ID !== folder.ID), [folder.ID, folders]);

  const confirmFolderMove = async () => {
    if (!selectedFolder) return;
    try {
      await moveFolder(selectedFolder).unwrap();
      enqueueSnackbar(`The ${type} has been moved`);
    } catch {
      enqueueSnackbar(`${type} move failed. Please try again...`);
    } finally {
      showConfirmModal(false);
      onClose();
    }
  };

  /**
   * On submit function called when the form is submitted and valid
   */
  const handleOnSubmit = (values: IFormData) => {
    const folderId = values.FolderId;
    setSelectedFolder({ ID: folder.ID, ParentID: folderId === HOME_FOLDER_ID ? null : folderId });
    showConfirmModal(true);
  };

  return (
    <>
      <Modal
        open
        header={`Move this ${type}`}
        data-testid="move-folder-modal"
        content={
          <>
            {isError && (
              <Box mb={2}>
                <Message type="error">There was an issue moving this {type}. Please try again.</Message>
              </Box>
            )}
            {!canMove ? (
              <Box mb={2}>
                <Alert severity="info">You do not have permission to move this {type}.</Alert>
              </Box>
            ) : (
              <>
                <Box mb={2}>
                  <Alert severity="warning">
                    Moving a {type} to a new location has the potential to change who can access & edit this {type} and its contents.
                  </Alert>
                </Box>
                <Form<IFormData>
                  onSubmit={handleOnSubmit}
                  initialValues={{ FolderId: folder.ParentID || HOME_FOLDER_ID }}
                  render={({ handleSubmit }) => {
                    return (
                      <form onSubmit={handleSubmit} id="move-entity-form">
                        <FolderPickerField name="FolderId" folders={foldersExceptSelf} account={folder.Account} />
                      </form>
                    );
                  }}
                />
              </>
            )}
          </>
        }
        actions={
          <>
            <Button data-testid="move-folder-cancel-button" color="primary" onClick={onClose} sx={{ marginRight: 1 }}>
              Cancel
            </Button>
            {canMove && (
              <Button form="move-entity-form" type="submit" data-testid="move-folder-save-button" color="primary" variant="contained">
                Move
              </Button>
            )}
          </>
        }
        onClose={onClose}
      />
      <ConfirmationModal
        isOpen={confirmModal}
        folderName={folder.Name}
        onConfirm={() => confirmFolderMove()}
        onClose={() => showConfirmModal(false)}
        isSaving={isSaving}
        type={type}
      />
    </>
  );
};

export default MoveFolderDialog;
